import { defineStore } from 'pinia'

import type { AiTag, GetAiDetailsResponse } from '~/types/ai-reviews'

type State = {
  aiSummaryText: string | null
  aiTags: AiTag[]
  areAiDetailsLoading: boolean
}

const initialState = (): State => ({ aiSummaryText: null, aiTags: [], areAiDetailsLoading: false })

export const useAiReviewsStore = defineStore('ai-reviews', {
  state: () => initialState(),
  actions: {
    async fetchAiDetails (productId: string): Promise<void> {
      if (this.areAiDetailsLoading) {
        return
      }

      this.areAiDetailsLoading = true

      try {
        const url = `https://api.itab.pro/reviews/api/v1/products/${productId}/ai-details`
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch AI details. Status: ${response.status}`)
        }

        const data: GetAiDetailsResponse = await response.json()
        this.aiSummaryText = data.summary_text
        this.aiTags = this.sortAiTags([...data.tags])
      } catch (error) {
        console.error('Failed to fetch AI details:', error)
      }

      this.areAiDetailsLoading = false
    },
    normalizeSentiment (sentiment: AiTag['sentiment']): '+' | '-' | 'neutral' {
      switch (sentiment) {
        case 'positive':
          return '+'
        case 'negative':
          return '-'
        default:
          return 'neutral'
      }
    },
    sortAiTags (aiTags: AiTag[]): AiTag[] {
      return aiTags.sort(
        (a: AiTag, b: AiTag): number => {
          const aSentiment = this.normalizeSentiment(a.sentiment)
          const bSentiment = this.normalizeSentiment(b.sentiment)

          if (aSentiment !== '+' && bSentiment === '+') {
            return 1
          }

          if (aSentiment === '+' && bSentiment !== '+') {
            return -1
          }

          return Math.sign(b.weight - a.weight)
        }
      )
    }
  }
})
